import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  Chip,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import TableSortLabel from "@mui/material/TableSortLabel";
import VisibilityIcon from "@mui/icons-material/Preview";
import FeatherIcon from "feather-icons-react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { toast } from "react-toastify";
import AppsIcon from "@mui/icons-material/Apps";
import IconButton from "@mui/material/IconButton";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import EventIcon from "@mui/icons-material/Event";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";
import {
  GetApplicationList,
  GetApplicationEmployees,
  GetEmployeeDropDown,
  UpdateApplication,
  GetAllDepartments,
} from "../../../services/admin";

const ApplicationList = () => {
  const [list, setList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [globalSearch, setGlobalSearch] = useState("");
  const [searchText, setSearchText] = useState({});
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [teamLeadOptions, setTeamLeadOptions] = useState([]);
  const [viewData, setViewData] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [frameworkOptions] = useState([
    "ASP.NET MVC",
    "ASP.NET Core",
    "React",
    "WebForms",
    "VB.NET",
  ]);

  const [vmCategoryOptions] = useState([
    {
      value: "Compliance and Regulation - vm-SSWhite-Static / 20.244.35.195",
      label: "Compliance and Regulation - vm-SSWhite-Static / 20.244.35.195",
    },
    {
      value: "Process Development - vm-sswhite-1 / 4.240.77.66",
      label: "Process Development - vm-sswhite-1 / 4.240.77.66",
    },
    {
      value: "Interdepartmental Support - vm-sswhite-2 / 4.240.79.86",
      label: "Interdepartmental Support - vm-sswhite-2 / 4.240.79.86",
    },
    {
      value: "Customer Support - vm-SSWhite / 20.198.3.2",
      label: "Customer Support - vm-SSWhite / 20.198.3.2",
    },
  ]);

  useEffect(() => {
    fetchApplications();
    fetchEmployees();
    fetchEmployeeDropDown();
    fetchDepartments();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await GetApplicationList();
      const formattedData = (response?.data || []).map((item) => ({
        application_ID: item.application_ID,
        application_Name: item.application_Name,
        description: item.description,
        scope: item.scope,
        dateAssigned: item.dateAssigned,
        actualStartDate: item.actualStartDate,
        estimatedEndDate: item.estimatedEndDate,
        actualEndDate: item.actualEndDate,
        teamLeadPersonID: item.teamLeadPersonID,
        appURL: item.appURL,
        deploymentServer: item.deploymentServer,
        applicableDepartment: item.applicableDepartment
          ? item.applicableDepartment.split(",").map((x) => x.trim())
          : [],
        applicationVersion: item.applicationVersion,
        frameWork: item.frameWork
          ? item.frameWork.split(",").map((x) => x.trim())
          : [],
        currentStatus: item.currentStatus || "",
        teamMembers: item.teamMembers
          ? item.teamMembers.split(",").map((x) => Number(x))
          : [],
        otherDeptEmployeeIDs: item.otherDeptEmployeeIDs
          ? item.otherDeptEmployeeIDs.split(",").map((x) => String(x))
          : [],
        ongoingProjectPercentage: item.ongoingProjectPercentage,
        remark: item.remark,
        IsActive: Boolean(item.isActive),
      }));
      setList(formattedData);
    } catch (error) {
      console.log(error);
      toast.error("Failed to load applications");
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await GetApplicationEmployees();
      const employees = response?.data || [];

      const itEmployees = employees.filter(
        (x) => x.department === "IT Software" || x.department === "IT",
      );

      const teamLeads = employees.filter(
        (x) =>
          x.designation?.toLowerCase().includes("manager") ||
          x.designation?.toLowerCase().includes("sr"),
      );

      // Remove duplicates from IT employees
      const uniqueITEmployees = [];
      const seenIds = new Set();

      itEmployees.forEach((x) => {
        if (!seenIds.has(x.personId)) {
          seenIds.add(x.personId);
          uniqueITEmployees.push(x);
        }
      });

      console.log(response.data);
      setEmployeeOptions(
        uniqueITEmployees.map((x) => ({
          id: x.personId,
          name: x.employeeName,
          designation: x.designation,
          department: x.department,
          isActive: x.isActive,
        })),
      );

      // Remove duplicates from team leads
      const uniqueTeamLeads = [];
      const seenLeadIds = new Set();

      teamLeads.forEach((x) => {
        if (!seenLeadIds.has(x.personId)) {
          seenLeadIds.add(x.personId);
          uniqueTeamLeads.push(x);
        }
      });

      setTeamLeadOptions(
        uniqueTeamLeads.map((x) => ({
          id: x.personId,
          name: x.employeeName.trim(),
          designation: x.designation,
          isActive: x.isActive,
        })),
      );
    } catch (error) {
      console.log("EMPLOYEE API ERROR =>", error);
      toast.error("Failed to load employees");
    }
  };

  const fetchEmployeeDropDown = async () => {
    try {
      const response = await GetEmployeeDropDown();
      const employees = response?.data?.buddyDropDown || [];
      setAllEmployees(
        employees.map((emp) => ({
          id: emp.personID,
          name: emp.buddyName,
          department: emp.department,
        })),
      );
    } catch (error) {
      console.log("EMPLOYEE DROPDOWN ERROR =>", error);
      toast.error("Failed to load employee dropdown");
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await GetAllDepartments();
      setDepartmentOptions(
        (response?.data || []).map((item) => item.departmentName),
      );
    } catch (error) {
      console.log(error);
      toast.error("Failed to load departments");
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return "";
    try {
      const [datePart] = date.split(" ");
      const [day, month, year] = datePart.split("-");
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  const formatDisplayDate = (date) => {
    if (!date) return "-";
    try {
      const [datePart] = date.split(" ");
      const [day, month, year] = datePart.split("-");
      return `${day}-${month}-${year}`;
    } catch {
      return "-";
    }
  };

  const validationSchema = Yup.object({
    applicationName: Yup.string()
      .trim()
      .required("Application name is required"),
    teamLeadPersonID: Yup.number().required("Team Lead is required"),
    teamMembers: Yup.array().min(1, "Select at least one team member"),
    ongoingProjectPercentage: Yup.number().min(0).max(100),
    appURL: Yup.string().url("Enter valid URL").nullable(),
  });

  const formik = useFormik({
    initialValues: {
      applicationName: "",
      description: "",
      scope: "",
      dateAssigned: "",
      actualStartDate: "",
      estimatedEndDate: "",
      actualEndDate: "",
      teamLeadPersonID: "",
      teamLeadDesignation: "",
      teamMembers: [],
      ongoingProjectPercentage: "",
      remark: "",
      appURL: "",
      selectedEmployee: [],
      deploymentServer: "",
      applicableDepartment: [],
      applicationVersion: "",
      frameWork: [],
      currentStatus: "",
      IsActive: true,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          Application_ID: editId || 0,
          Application_Name: values.applicationName?.trim() || "",
          Description: values.description?.trim() || "",
          Scope: values.scope?.trim() || "",
          DateAssigned: values.dateAssigned,
          ActualStartDate: values.actualStartDate,
          EstimatedEndDate: values.estimatedEndDate,
          ActualEndDate: values.actualEndDate,
          TeamLeadPersonID: values.teamLeadPersonID,
          TeamLeadDesignation: values.teamLeadDesignation,
          TeamMembers: values.teamMembers.join(","),
          OtherDeptEmployeeIDs: values.selectedEmployee.join(","),
          OngoingProjectPercentage: values.ongoingProjectPercentage || 0,
          Remark: values.remark?.trim() || "",
          AppURL: values.appURL?.trim() || "",
          IsActive: values.IsActive ? 1 : 0,
          DeploymentServer: values.deploymentServer?.trim() || "",
          ApplicableDepartment: values.applicableDepartment.join(","),
          ApplicationVersion: values.applicationVersion?.trim() || "",
          FrameWork: values.frameWork.join(","),
          CurrentStatus: values.currentStatus,
        };

        await UpdateApplication(payload);
        toast.success("Application updated successfully");
        fetchApplications();
        resetForm();
        setEditId(null);
        setOpenEdit(false);
        setEditItem(null);
      } catch (error) {
        console.log("ERROR", error);
        toast.error(error.response?.data || "Update failed");
      }
    },
  });

  const handleEditClick = (item) => {
    setEditId(item.application_ID);
    setEditItem(item);
    const selectedLead = teamLeadOptions.find(
      (x) => Number(x.id) === Number(item.teamLeadPersonID),
    );

    // Ensure team members are unique
    let teamMembers = [];
    if (typeof item.teamMembers === "string") {
      teamMembers = [
        ...new Set(item.teamMembers.split(",").map((x) => Number(x))),
      ];
    } else if (Array.isArray(item.teamMembers)) {
      teamMembers = [...new Set(item.teamMembers.map((x) => Number(x)))];
    }

    formik.setValues({
      applicationName: item.application_Name || "",
      description: item.description || "",
      scope: item.scope || "",
      dateAssigned: formatDateForInput(item.dateAssigned),
      actualStartDate: formatDateForInput(item.actualStartDate),
      estimatedEndDate: formatDateForInput(item.estimatedEndDate),
      actualEndDate: formatDateForInput(item.actualEndDate),
      teamLeadPersonID: item.teamLeadPersonID || "",
      teamLeadDesignation: selectedLead?.designation || "",
      deploymentServer: item.deploymentServer || "",
      applicableDepartment: item.applicableDepartment
        ? Array.isArray(item.applicableDepartment)
          ? item.applicableDepartment
          : item.applicableDepartment.split(",").map((x) => x.trim())
        : [],
      applicationVersion: item.applicationVersion || "",
      frameWork: item.frameWork
        ? Array.isArray(item.frameWork)
          ? item.frameWork
          : item.frameWork.split(",").map((x) => x.trim())
        : [],
      currentStatus: item.currentStatus || "",
      teamMembers: teamMembers,
      selectedEmployee: item.otherDeptEmployeeIDs || [],
      ongoingProjectPercentage: item.ongoingProjectPercentage || "",
      remark: item.remark || "",
      appURL: item.appURL || "",
      IsActive: item.IsActive,
    });
    setOpenEdit(true);
  };

  const handleToggleStatus = async (item, checked) => {
    setList((prev) =>
      prev.map((x) =>
        x.application_ID === item.application_ID
          ? { ...x, IsActive: checked }
          : x,
      ),
    );
    try {
      const payload = {
        Application_ID: item.application_ID,
        Application_Name: item.application_Name,
        IsActive: checked ? 1 : 0,
      };
      await UpdateApplication(payload);
      toast.success(`Application ${checked ? "Activated" : "Deactivated"}`);
    } catch {
      toast.error("Failed to update status");
      setList((prev) =>
        prev.map((x) =>
          x.application_ID === item.application_ID
            ? { ...x, IsActive: !checked }
            : x,
        ),
      );
    }
  };

  const handleView = (item) => {
    setViewData(item);
    setOpenView(true);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleResetFilters = () => {
    setGlobalSearch("");
    setSearchText({});
    setShowAllFilters(false);
    setPage(0);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditId(null);
    setEditItem(null);
    formik.resetForm();
  };

  const filteredList = list.filter((item) => {
    const globalMatch = Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(globalSearch.toLowerCase());
    const columnMatch = Object.keys(searchText).every((key) => {
      if (!searchText[key]) return true;
      const value = item[key]?.toString().toLowerCase() || "";
      return value.includes(searchText[key].toLowerCase());
    });
    return globalMatch && columnMatch;
  });

  const sortedList = [...filteredList].sort((a, b) => {
    if (!orderBy) return 0;
    let valueA = a[orderBy];
    let valueB = b[orderBy];
    if (typeof valueA === "string") valueA = valueA.toLowerCase();
    if (typeof valueB === "string") valueB = valueB.toLowerCase();
    if (valueA < valueB) return order === "asc" ? -1 : 1;
    if (valueA > valueB) return order === "asc" ? 1 : -1;
    return 0;
  });

  const renderHeader = (label, field) => (
    <TableCell>
      <Box display="flex" alignItems="center" gap={0.5}>
        <TableSortLabel
          active={orderBy === field}
          direction={orderBy === field ? order : "asc"}
          onClick={() => handleSort(field)}
        >
          {label}
        </TableSortLabel>
      </Box>
    </TableCell>
  );

  return (
    <>
      <Card sx={{ mt: 2, p: 0, overflow: "hidden" }}>
        {/* HEADER */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <Box display="flex" alignItems="center">
            <FeatherIcon icon="alert-circle" width="18" />
            <Box sx={{ ml: 1 }}>Existing Applications</Box>
          </Box>

          {/* SEARCH + FILTER */}
          <Box display="flex" alignItems="center">
            <TextField
              size="small"
              placeholder="Search..."
              value={globalSearch}
              onFocus={() => {
                setShowAllFilters(false);
                setSearchText({});
              }}
              onChange={(e) => {
                setGlobalSearch(e.target.value);
                setPage(0);
              }}
              sx={{
                width: 220,
                backgroundColor: "white",
                borderRadius: 1,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {globalSearch && (
              <CloseIcon
                sx={{
                  cursor: "pointer",
                  ml: 1,
                }}
                onClick={() => setGlobalSearch("")}
              />
            )}
            <FilterAltIcon
              sx={{
                cursor: "pointer",
                ml: 2,
                color: showAllFilters
                  ? (theme) => theme.palette.primary.main
                  : "text.primary",
                transition: "0.2s",
              }}
              onClick={() => setShowAllFilters(!showAllFilters)}
            />
            <Button
              variant="outlined"
              size="small"
              sx={{ ml: 2, height: 32 }}
              onClick={handleResetFilters}
            >
              Reset Filter
            </Button>
          </Box>
        </Box>

        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#FAFBFB" }}>
                  {renderHeader("ID", "application_ID")}
                  {renderHeader("Application Name", "application_Name")}
                  <TableCell>Team Lead</TableCell>
                  <TableCell>Team Members</TableCell>
                  {renderHeader("Progress %", "ongoingProjectPercentage")}
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>

                {showAllFilters && (
                  <TableRow>
                    {["application_ID", "application_Name"].map((field) => (
                      <TableCell key={field}>
                        <TextField
                          size="small"
                          fullWidth
                          value={searchText[field] || ""}
                          onChange={(e) =>
                            setSearchText({
                              ...searchText,
                              [field]: e.target.value,
                            })
                          }
                          placeholder="Search..."
                          InputProps={{
                            endAdornment: searchText[field] && (
                              <InputAdornment position="end">
                                <CloseIcon
                                  fontSize="small"
                                  sx={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    setSearchText({
                                      ...searchText,
                                      [field]: "",
                                    })
                                  }
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {sortedList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No applications found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {(rowsPerPage > 0
                  ? sortedList.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : sortedList
                ).map((item) => (
                  <TableRow key={item.application_ID}>
                    <TableCell>{item.application_ID}</TableCell>
                    <TableCell>{item.application_Name}</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      {(() => {
                        const lead =
                          teamLeadOptions.find(
                            (x) =>
                              Number(x.id) === Number(item.teamLeadPersonID),
                          ) ||
                          employeeOptions.find(
                            (x) =>
                              Number(x.id) === Number(item.teamLeadPersonID),
                          );
                        return lead ? (
                          <Chip
                            sx={{
                              mb: 1,
                              bgcolor: "transparent",
                              color: "primary.main",
                              border: "1px solid",
                              borderColor: "primary.main",
                            }}
                            avatar={
                              <Avatar
                                sx={{
                                  bgcolor: "primary.main",
                                  color: "primary.contrastText",
                                }}
                              >
                                {lead.name?.charAt(0)}
                              </Avatar>
                            }
                            label={lead.name}
                          />
                        ) : (
                          "-"
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        {item.teamMembers?.length > 0 ? (
                          [
                            ...new Set(
                              item.teamMembers.map((id) => Number(id)),
                            ),
                          ].map((id) => {
                            const emp = employeeOptions.find(
                              (x) => Number(x.id) === Number(id),
                            );
                            return emp ? (
                              <Chip
                                key={id}
                                label={emp.name}
                                size="small"
                                avatar={<Avatar>{emp.name.charAt(0)}</Avatar>}
                                sx={{
                                  bgcolor: emp.isActive ? "#E8F5E9" : "#F5F5F5",
                                  color: emp.isActive ? "#2E7D32" : "#757575",
                                  border: "1px solid",
                                  borderColor: emp.isActive
                                    ? "#A5D6A7"
                                    : "#d6d6d6",
                                }}
                              />
                            ) : null;
                          })
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{item.ongoingProjectPercentage}%</TableCell>
                    <TableCell>
                      <Tooltip title={item.IsActive ? "Active" : "Inactive"}>
                        <Switch
                          checked={item.IsActive}
                          size="small"
                          onChange={(e) =>
                            handleToggleStatus(item, e.target.checked)
                          }
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                      >
                        <Tooltip title="View">
                          <VisibilityIcon
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleView(item)}
                          />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <EditIcon
                            sx={{
                              cursor: "pointer",
                              color: "primary.main",
                            }}
                            onClick={() => handleEditClick(item)}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 25, 50]}
            component="div"
            count={filteredList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>

      {/* VIEW DIALOG */}
      {/* VIEW DIALOG */}
      <Dialog
        open={openView}
        onClose={() => setOpenView(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 2,
            bgcolor: "primary.main",
            color: "white",
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "relative",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <AppsIcon sx={{ color: "white" }} fontSize="medium" />
            <Typography variant="h6" fontWeight={800} color="white">
              Application Details
            </Typography>
          </Box>

          {viewData?.application_Name && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              <Chip
                label={viewData.application_Name}
                size="large"
                icon={<LinkIcon sx={{ color: "white" }} />}
                onClick={() =>
                  window.open(
                    viewData.application_Link || viewData.appURL,
                    "_blank",
                  )
                }
                sx={{
                  fontSize: "1rem",
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  borderColor: "rgba(255,255,255,0.3)",
                  fontWeight: 540,
                  cursor: "pointer",
                  "& .MuiChip-icon": { color: "white" },
                }}
                variant="outlined"
              />
              <Box display="flex" alignItems="center" gap={2} sx={{ mt: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "white",
                    fontWeight: 500,
                    opacity: 0.9,
                  }}
                >
                  v{viewData.applicationVersion || "—"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.5)" }}
                >
                  •
                </Typography>
                <Chip
                  label={viewData.currentStatus || "—"}
                  size="small"
                  color={
                    viewData.currentStatus === "Completed"
                      ? "success"
                      : viewData.currentStatus === "In Progress"
                      ? "warning"
                      : viewData.currentStatus === "On Hold"
                      ? "error"
                      : "default"
                  }
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.5)" }}
                >
                  •
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "white",
                    fontWeight: 500,
                    opacity: 0.9,
                  }}
                >
                  {Array.isArray(viewData.frameWork) &&
                  viewData.frameWork.length > 0
                    ? viewData.frameWork.join(", ")
                    : "—"}
                </Typography>
              </Box>
            </Box>
          )}

          <IconButton
            onClick={() => setOpenView(false)}
            size="medium"
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {viewData && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          height: "100%",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={1.5}
                        >
                          <EventIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Timeline
                          </Typography>
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              Assigned
                            </Typography>
                            <Typography variant="body2">
                              {formatDisplayDate(viewData.dateAssigned) || "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              Actual Start
                            </Typography>
                            <Typography variant="body2">
                              {formatDisplayDate(viewData.actualStartDate) ||
                                "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              Estimated End
                            </Typography>
                            <Typography variant="body2">
                              {formatDisplayDate(viewData.estimatedEndDate) ||
                                "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              Actual End
                            </Typography>
                            <Typography variant="body2">
                              {formatDisplayDate(viewData.actualEndDate) || "—"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          height: "100%",
                        }}
                      >
                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              mb={0.5}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <TrendingUpIcon
                                  color="success"
                                  fontSize="small"
                                />
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                >
                                  Progress
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={500}
                                >
                                  Server:
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {viewData.deploymentServer || "—"}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label={`${
                                viewData.ongoingProjectPercentage || 0
                              }%`}
                              color="success"
                              size="small"
                              sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                            />
                            <LinearProgress
                              variant="determinate"
                              value={viewData.ongoingProjectPercentage || 0}
                              sx={{
                                mt: 0.5,
                                height: 6,
                                borderRadius: 1,
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 1,
                                  boxShadow:
                                    "0 0 8px 2px rgba(76, 175, 80, 0.3)",
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <PersonIcon color="primary" fontSize="small" />
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color="text.secondary"
                              >
                                Team Lead:
                              </Typography>
                              {(() => {
                                const teamLead =
                                  teamLeadOptions.find(
                                    (x) =>
                                      Number(x.id) ===
                                      Number(viewData.teamLeadPersonID),
                                  ) ||
                                  employeeOptions.find(
                                    (x) =>
                                      Number(x.id) ===
                                      Number(viewData.teamLeadPersonID),
                                  );
                                return (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {teamLead?.name?.charAt(0) || "—"}
                                    </Avatar>
                                    <Typography
                                      variant="body2"
                                      fontWeight={500}
                                    >
                                      {teamLead?.name || "—"}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Applicable Departments
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.5,
                            display: "flex",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          {Array.isArray(viewData.applicableDepartment) &&
                          viewData.applicableDepartment.length > 0 ? (
                            viewData.applicableDepartment.map((item) => (
                              <Chip
                                key={item}
                                label={item}
                                size="small"
                                variant="outlined"
                                color="secondary"
                              />
                            ))
                          ) : (
                            <Typography variant="body2">—</Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    {/* Team Members */}
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          height: "100%",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <GroupIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Team Members
                          </Typography>
                          {viewData.teamMembers?.length > 0 && (
                            <Chip
                              label={
                                [
                                  ...new Set(
                                    viewData.teamMembers.map((id) =>
                                      Number(id),
                                    ),
                                  ),
                                ].length
                              }
                              size="small"
                              color="primary"
                              sx={{ ml: "auto" }}
                            />
                          )}
                        </Box>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {viewData.teamMembers?.length > 0 ? (
                            [
                              ...new Set(
                                viewData.teamMembers.map((id) => Number(id)),
                              ),
                            ].map((id) => {
                              const emp = employeeOptions.find(
                                (x) => String(x.id) === String(id),
                              );
                              return (
                                <Chip
                                  key={id}
                                  label={emp?.name || id}
                                  size="small"
                                  variant="outlined"
                                />
                              );
                            })
                          ) : (
                            <Typography variant="body2">—</Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          height: "100%",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <GroupIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Other Department Employees
                          </Typography>
                          {viewData.otherDeptEmployeeIDs?.length > 0 && (
                            <Chip
                              label={viewData.otherDeptEmployeeIDs.length}
                              size="small"
                              color="primary"
                              variant="contained"
                              sx={{ ml: "auto" }}
                            />
                          )}
                        </Box>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {viewData.otherDeptEmployeeIDs?.length > 0 ? (
                            viewData.otherDeptEmployeeIDs.map((id) => {
                              const emp = allEmployees.find(
                                (x) => String(x.id) === String(id),
                              );
                              return emp ? (
                                <Chip
                                  key={id}
                                  label={emp.name}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              ) : null;
                            })
                          ) : (
                            <Typography variant="body2">—</Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* NEW TECH DETAILS SECTION */}
                {/* <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <CodeIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Tech Details
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          Deployment Server
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {viewData.deploymentServer || "Azure Cloud"}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          VM Name
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {viewData.vmName || "Customer Support - vm-SSWhite"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          Public IP
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {viewData.publicIP || "20.198.3.2"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          Framework
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {Array.isArray(viewData.frameWork) &&
                          viewData.frameWork.length > 0
                            ? viewData.frameWork.join(", ")
                            : "ASP.NET Core 8 + React"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          Database
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {viewData.database || "Azure SQL"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          Hosting
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {viewData.hosting || "Azure VM"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          Environment
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {viewData.environment || "Production"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          Current Version
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {viewData.applicationVersion || "v2.0"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid> */}

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          maxHeight: 120,
                          overflow: "auto",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Description
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.5,
                            lineHeight: 1.6,
                            textAlign: "justify",
                          }}
                        >
                          {viewData.description || "No description provided."}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          maxHeight: 120,
                          overflow: "auto",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Scope
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.5,
                            lineHeight: 1.6,
                          }}
                        >
                          {viewData.scope || "No scope defined."}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      maxHeight: 150,
                      overflow: "auto",
                      wordWrap: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                      Remark
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {viewData.remark || "—"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        />
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog
        open={openEdit}
        onClose={handleCloseEdit}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 2,
            bgcolor: "primary.main",
            color: "white",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <EditIcon sx={{ color: "white" }} fontSize="medium" />
            <Typography variant="h6" fontWeight={800} color="white">
              Edit Application
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseEdit}
            size="medium"
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="flex-start">
            {/* Application Name */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Application Name</CustomFormLabel>
              <CustomTextField
                fullWidth
                size="small"
                placeholder="Name of the application"
                name="applicationName"
                value={formik.values.applicationName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.applicationName &&
                  Boolean(formik.errors.applicationName)
                }
                helperText={
                  formik.touched.applicationName &&
                  formik.errors.applicationName
                }
              />
            </Grid>

            {/* Description */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Description</CustomFormLabel>
              <CustomTextField
                fullWidth
                size="small"
                placeholder="Description of the application"
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
              />
            </Grid>

            {/* Scope */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Scope</CustomFormLabel>
              <CustomTextField
                fullWidth
                size="small"
                name="scope"
                value={formik.values.scope}
                onChange={formik.handleChange}
                placeholder="Scope of the application"
              />
            </Grid>

            {/* Progress */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Project %</CustomFormLabel>
              <CustomTextField
                fullWidth
                size="small"
                type="number"
                name="ongoingProjectPercentage"
                value={formik.values.ongoingProjectPercentage}
                onChange={formik.handleChange}
                placeholder="Percentage of ongoing project"
              />
            </Grid>

            {/* Team Lead */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Team Lead</CustomFormLabel>
              <FormControl fullWidth size="small">
                <Select
                  name="teamLeadPersonID"
                  value={formik.values.teamLeadPersonID}
                  displayEmpty
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedLead = teamLeadOptions.find(
                      (x) => Number(x.id) === Number(selectedId),
                    );
                    formik.setFieldValue("teamLeadPersonID", selectedId);
                    formik.setFieldValue(
                      "teamLeadDesignation",
                      selectedLead?.designation || "",
                    );
                  }}
                  error={
                    formik.touched.teamLeadPersonID &&
                    Boolean(formik.errors.teamLeadPersonID)
                  }
                >
                  <MenuItem value="" disabled sx={{ color: "text.secondary" }}>
                    Select Team Lead
                  </MenuItem>
                  {teamLeadOptions.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Team Lead Designation</CustomFormLabel>
              <CustomTextField
                fullWidth
                size="small"
                value={formik.values.teamLeadDesignation}
                disabled
              />
            </Grid>

            {/* Team Members */}
            {/* Team Members in Edit Dialog */}
            <Grid item lg={2} md={12} sm={12}>
              <CustomFormLabel>Team Members</CustomFormLabel>
              <Autocomplete
                multiple
                limitTags={1}
                options={employeeOptions}
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) =>
                  Number(option.id) === Number(value.id)
                }
                getOptionLabel={(option) =>
                  `${option.name} (${option.designation})`
                }
                value={formik.values.teamMembers
                  .map((id) =>
                    employeeOptions.find(
                      (emp) => Number(emp.id) === Number(id),
                    ),
                  )
                  .filter(Boolean)}
                onChange={(event, value) => {
                  // Use a Set to ensure unique IDs
                  const uniqueIds = [
                    ...new Set(value.map((item) => Number(item.id))),
                  ];

                  formik.setFieldValue("teamMembers", uniqueIds);
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} />
                    {option.name} ({option.designation})
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select Team Members"
                    error={
                      formik.touched.teamMembers &&
                      Boolean(formik.errors.teamMembers)
                    }
                    helperText={
                      formik.touched.teamMembers && formik.errors.teamMembers
                    }
                  />
                )}
              />
            </Grid>
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>OtherDept Employees</CustomFormLabel>
              <Autocomplete
                multiple
                limitTags={1}
                options={allEmployees}
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                getOptionLabel={(option) => option.name || ""}
                value={allEmployees.filter((x) =>
                  formik.values.selectedEmployee?.includes(String(x.id)),
                )}
                onChange={(event, value) => {
                  formik.setFieldValue(
                    "selectedEmployee",
                    value.map((x) => String(x.id)),
                  );
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select Employees"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={2}>
              <CustomFormLabel>Application URL</CustomFormLabel>
              <CustomTextField
                fullWidth
                size="small"
                name="appURL"
                placeholder="https://sswhite.co"
                value={formik.values.appURL}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.appURL && Boolean(formik.errors.appURL)}
                helperText={formik.touched.appURL && formik.errors.appURL}
              />
            </Grid>

            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>VM Category</CustomFormLabel>
              <FormControl fullWidth size="small">
                <Select
                  name="deploymentServer"
                  value={formik.values.deploymentServer}
                  onChange={formik.handleChange}
                  displayEmpty
                >
                  <MenuItem value="">Select VM Category</MenuItem>
                  {vmCategoryOptions.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Applicable Departments</CustomFormLabel>
              <Autocomplete
                multiple
                limitTags={1}
                options={departmentOptions}
                disableCloseOnSelect
                value={formik.values.applicableDepartment}
                onChange={(e, value) =>
                  formik.setFieldValue("applicableDepartment", value)
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} />
                    {option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Applicable Department"
                  />
                )}
              />
            </Grid>

            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Application Version</CustomFormLabel>
              <CustomTextField
                fullWidth
                size="small"
                name="applicationVersion"
                value={formik.values.applicationVersion}
                onChange={formik.handleChange}
                placeholder="Version"
              />
            </Grid>

            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Framework Used</CustomFormLabel>
              <Autocomplete
                multiple
                limitTags={1}
                options={frameworkOptions}
                disableCloseOnSelect
                value={formik.values.frameWork}
                onChange={(e, value) =>
                  formik.setFieldValue("frameWork", value)
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} />
                    {option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select Framework"
                  />
                )}
              />
            </Grid>

            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Current Status</CustomFormLabel>
              <FormControl fullWidth size="small">
                <Select
                  name="currentStatus"
                  value={formik.values.currentStatus}
                  onChange={formik.handleChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Status</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="InProgress">InProgress</MenuItem>
                  <MenuItem value="Not In use">Not In use</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date Assigned */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Date Assigned</CustomFormLabel>
              <TextField
                type="date"
                fullWidth
                size="small"
                name="dateAssigned"
                value={formik.values.dateAssigned || ""}
                onChange={formik.handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Actual Start Date */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Actual Start Date</CustomFormLabel>
              <TextField
                type="date"
                fullWidth
                size="small"
                name="actualStartDate"
                value={formik.values.actualStartDate}
                onChange={formik.handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Estimated End Date */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Estimated End Date</CustomFormLabel>
              <TextField
                type="date"
                fullWidth
                size="small"
                name="estimatedEndDate"
                value={formik.values.estimatedEndDate}
                onChange={formik.handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Actual End Date */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Actual End Date</CustomFormLabel>
              <TextField
                type="date"
                fullWidth
                size="small"
                name="actualEndDate"
                value={formik.values.actualEndDate}
                onChange={formik.handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Remark */}
            <Grid item lg={4} md={8} sm={12}>
              <CustomFormLabel>Remark</CustomFormLabel>
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={1}
                name="remark"
                placeholder="Remark for the app"
                value={formik.values.remark}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                sx={{
                  "& .MuiInputBase-root": {
                    padding: "8px 14px",
                    alignItems: "flex-start",
                  },
                  "& textarea": {
                    resize: "vertical",
                    minHeight: "40px !important",
                    maxHeight: "150px !important",
                    overflow: "auto !important",
                    padding: "4px 0 !important",
                    lineHeight: 1.5,
                  },
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item lg={2} md={6} sm={12}>
              <CustomFormLabel>Status</CustomFormLabel>
              <Box display="flex" alignItems="center" mt={1}>
                <Switch
                  checked={formik.values.IsActive}
                  onChange={(e) =>
                    formik.setFieldValue("IsActive", e.target.checked)
                  }
                />
                <Typography variant="body2">Active</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Button onClick={handleCloseEdit} variant="outlined">
            Cancel
          </Button>
          <Button onClick={formik.handleSubmit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplicationList;
